import React, {
  useState,
  useCallback,
  useRef,
  useContext,
  useEffect,
} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  CardActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  IconButton,
  Chip,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PreviewIcon from "@mui/icons-material/Preview";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { jwtDecode } from "jwt-decode";

import { postDocumento } from "../../../componentes/http/actions/DocumentoAction";
import { postOrganismoEmisor } from "../../../componentes/http/actions/OrganismoEmisorAction";
import { postEstadoTramitacion } from "../../../componentes/http/actions/EstadoTramitacionAction";
import { postClasificacionDocumentos } from "../../../componentes/http/actions/ClasificacionDocumentoAction";
import { postRubro } from "../../../componentes/http/actions/RubroAction";
import { postClientes, getClientesAutomaticos } from "../../../componentes/http/actions/ClientesAction";
import { postPais } from "../../../componentes/http/actions/PaisAction";
import DataLoader from "../../../componentes/data/DataLoader";
import CheckboxGroup from "../../../componentes/data/CheckBox";
import AutocompleteWithAddButton from "../../../componentes/common/AutocompleteAddButton";
import MantenedorQuickAddModal from "../../../componentes/common/MantenedorAddModal";
import { NotificacionContext } from "../../../componentes/common/NotificacionContext";

const truncarTexto = (texto = "", maxLength = 60) =>
  texto.length > maxLength ? texto.substring(0, maxLength) + "..." : texto;
 
const mapearOrganismo = (reguladorNombre, organismos) => {
  if (!reguladorNombre || !organismos?.length) return null;
  const lower = reguladorNombre.toLowerCase().trim();
  return (
    organismos.find(
      (o) =>
        o.nombre?.toLowerCase().trim() === lower ||
        o.sigla?.toLowerCase().trim() === lower ||
        o.nombreAbreviado?.toLowerCase().trim() === lower
    ) || null
  );
};
 

const mapearTipoDocumento = (tipoNombre, tipos) => {
  if (!tipoNombre || !tipos?.length) return null;
  const lower = tipoNombre.toLowerCase().trim();
  return tipos.find((t) => t.nombre?.toLowerCase().trim() === lower) || null;
};
 

const mapearRubros = (rubrosScaper, rubrosAN) => {
  if (!rubrosScaper?.length || !rubrosAN?.length) return [];
  return rubrosAN
    .filter((r) =>
      rubrosScaper.some(
        (rs) => rs.nombre?.toLowerCase().trim() === r.nombre?.toLowerCase().trim()
      )
    )
    .map((r) => r.id);
};
 

const mapearPais = (paisNombre, paises) => {
  if (!paisNombre || !paises?.length) return null;
  const lower = paisNombre.toLowerCase().trim();
  return paises.find((p) => p.nombre?.toLowerCase().trim() === lower) || null;
};
 
// ─── Componente Principal ────────────────────────────────────────────────────
const RegistrarDesdeScraperModal = ({ open, onClose, normativaScraper, onSuccess }) => {
  const { mostrarNotificacion } = useContext(NotificacionContext);
  const dataLoaderRef = useRef(null);
  const decoded = jwtDecode(localStorage.getItem("token") || "{}");
  const userId = decoded["oid"] || "";
 
  // ── Datos cargados desde DataLoader ──
  const [loadedData, setLoadedData] = useState({
    organismos: [],
    tiposDocumento: [],
    estadosTramitacion: [],
    rubros: [],
    clasificaciones: [],
    clientes: [],
    paises: [],
  });
  const [tiposDocumentoFiltrados, setTiposDocumentoFiltrados] = useState([]);
 
  // ── Estado del formulario ──
  const [formData, setFormData] = useState({
    nombre: "",
    nombreAbreviado: "",
    descripcion: "",
    descripcionAbreviado: "",
    vigencia: "Desde su publicación",
    codigo: "",
    url: "",
    enlaceResumen: "",
    creadoPor: "",
    organismoEmisorId: "",
    tipoDocumentoId: "",
    estadoTramitacionId: "",
    paisId: 1,
    fechaPublicacion: null,
    actualizacionNorma: false,
    rubroId: [],
    clasificacionDocumentoId: [],
    clienteId: [],
    compromisos: [],
    obligaciones: [],
  });
 
  const [fecha, setFecha] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [quickAddLoading, setQuickAddLoading] = useState(false);
  const [clientesAutomaticos, setClientesAutomaticos] = useState([]);
 
  // Compromisos / Obligaciones
  const [mostrarSeccionCompromisos, setMostrarSeccionCompromisos] = useState(false);
  const [mostrarSeccionObligaciones, setMostrarSeccionObligaciones] = useState(false);
  const [compromisoActual, setCompromisoActual] = useState("");
  const [obligacionActual, setObligacionActual] = useState("");
 
  // Validaciones
  const [validationErrors, setValidationErrors] = useState({
    rubros: false,
    clientes: false,
    clasificaciones: false,
    compromisos: false,
    obligaciones: false,
  });
 
  // Modales de quick-add
  const [modalState, setModalState] = useState({
    organismoEmisor: false,
    estadoTramitacion: false,
    rubro: false,
    clasificacion: false,
    cliente: false,
    pais: false,
  });
 
  // Campos auto-mapeados (para mostrar badge informativo)
  const [camposMapeados, setCamposMapeados] = useState([]);
 
  // ── Mapeo inicial cuando se abre el modal ──────────────────────────────────
  useEffect(() => {
    if (!open || !normativaScraper) return;
 
    setFormData({
      nombre: "",
      nombreAbreviado: "",
      descripcion: "",
      descripcionAbreviado: "",
      vigencia: "Desde su publicación",
      codigo: "",
      url: "",
      enlaceResumen: "",
      creadoPor: "",
      organismoEmisorId: "",
      tipoDocumentoId: "",
      estadoTramitacionId: "",
      paisId: 1,
      fechaPublicacion: null,
      actualizacionNorma: false,
      rubroId: [],
      clasificacionDocumentoId: [],
      clienteId: [],              
      compromisos: [],
      obligaciones: [],
    });
    setFecha(null);
    setClientesAutomaticos([]);
    setCompromisoActual("");
    setObligacionActual("");
    setMostrarSeccionCompromisos(false);
    setMostrarSeccionObligaciones(false);
    setValidationErrors({ rubros: false, clientes: false, clasificaciones: false, compromisos: false, obligaciones: false });
    setCamposMapeados([]);
  }, [open, normativaScraper]);
 
  // ── Aplicar mapeo cuando se cargan los datos Y hay normativa ──────────────
  useEffect(() => {
    if (!normativaScraper || !loadedData.organismos.length) return;
 
    const mapeados = [];
 
    // Nombre
    const nombre = normativaScraper.nombre || "";
 
    // Fecha
    const fechaDate = normativaScraper.fecha ? new Date(normativaScraper.fecha) : null;
    const fechaValida =
      fechaDate && !isNaN(fechaDate.getTime()) && fechaDate.getFullYear() > 1900;
 
    // Organismo
    const organismoMapeado = mapearOrganismo(normativaScraper.regulador, loadedData.organismos);
    if (organismoMapeado) mapeados.push(`Organismo: ${organismoMapeado.nombre}`);
 
    // País
    const paisMapeado = mapearPais(normativaScraper.pais, loadedData.paises);
    if (paisMapeado) mapeados.push(`País: ${paisMapeado.nombre}`);
 
    // Tipos filtrados por organismo
    let tiposFiltrados = loadedData.tiposDocumento;
    if (organismoMapeado?.idTipoDocumento?.length) {
      tiposFiltrados = loadedData.tiposDocumento.filter((t) =>
        organismoMapeado.idTipoDocumento.includes(t.id)
      );
    }
    setTiposDocumentoFiltrados(tiposFiltrados);
 
    // Tipo documento
    const tipoMapeado = mapearTipoDocumento(normativaScraper.tipoDocumento, tiposFiltrados);
    if (tipoMapeado) mapeados.push(`Tipo: ${tipoMapeado.nombre}`);
 
    // Rubros
    const rubrosMapeados = mapearRubros(normativaScraper.rubros, loadedData.rubros);
    if (rubrosMapeados.length)
      mapeados.push(`Rubros: ${rubrosMapeados.length} seleccionado(s)`);
 
    // Compromisos del scraper
    const compromisosScraper = (normativaScraper.compromisos || []).map((c) => ({
      descripcion: c.descripcion || "",
    }));
    const obligacionesScraper = (normativaScraper.obligaciones || []).map((o) => ({
      descripcion: o.descripcion || "",
    }));
    if (compromisosScraper.length) {
      setMostrarSeccionCompromisos(true);
      mapeados.push(`Compromisos: ${compromisosScraper.length}`);
    }
    if (obligacionesScraper.length) {
      setMostrarSeccionObligaciones(true);
      mapeados.push(`Obligaciones: ${obligacionesScraper.length}`);
    }
 
    setFecha(fechaValida ? fechaDate : null);
    if (fechaValida) mapeados.push("Fecha de publicación");
    if (nombre) mapeados.push("Nombre");
    if (normativaScraper.linkSitioWeb) mapeados.push("URL");
    if (normativaScraper.descripcion) mapeados.push("Descripción");
 
    setFormData((prev) => ({
      ...prev,
      nombre,
      nombreAbreviado: nombre.substring(0, 400),
      descripcion: normativaScraper.descripcion || "",
      descripcionAbreviado: (normativaScraper.descripcion || "").substring(0, 400),
      url: normativaScraper.linkSitioWeb || "",
      organismoEmisorId: organismoMapeado?.id || "",
      tipoDocumentoId: tipoMapeado?.id || "",
      paisId: paisMapeado?.id || 1,
      rubroId: rubrosMapeados,
      compromisos: compromisosScraper,
      obligaciones: obligacionesScraper,
    }));
 
    setCamposMapeados(mapeados);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normativaScraper, loadedData.organismos]);
 
  // ── DataLoader callback ────────────────────────────────────────────────────
  const handleDataLoaded = useCallback((data) => {
    setLoadedData(data);
    setTiposDocumentoFiltrados(data.tiposDocumento || []);
  }, []);
 
  // ── Filtrar tipos por organismo ────────────────────────────────────────────
  const filtrarTiposDocumento = useCallback(
    (organismoId) => {
      if (!organismoId) {
        setTiposDocumentoFiltrados(loadedData.tiposDocumento || []);
        return;
      }
      const org = loadedData.organismos.find((o) => o.id === organismoId);
      if (org?.idTipoDocumento?.length) {
        setTiposDocumentoFiltrados(
          loadedData.tiposDocumento.filter((t) => org.idTipoDocumento.includes(t.id))
        );
      } else {
        setTiposDocumentoFiltrados(loadedData.tiposDocumento || []);
      }
    },
    [loadedData.organismos, loadedData.tiposDocumento]
  );
 
  // ── Handlers genéricos ────────────────────────────────────────────────────
  const handleChange = useCallback((event) => {
    const { name, value, checked, type } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }, []);
 
  const handleNombreChange = useCallback((event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      nombre: value,
      nombreAbreviado: value.substring(0, 400),
    }));
  }, []);
 
  const handleDescripcionChange = useCallback((event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      descripcion: value,
      descripcionAbreviado: value.substring(0, 400),
    }));
  }, []);
 
  const handleOrganismoChange = useCallback(
    (event, newValue) => {
      const organismoId = newValue?.id || "";
      setFormData((prev) => ({
        ...prev,
        organismoEmisorId: organismoId,
        tipoDocumentoId: "",
      }));
      filtrarTiposDocumento(organismoId);
    },
    [filtrarTiposDocumento]
  );
 
  const handleCheckboxChange = useCallback((event, id) => {
    const { name, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked ? [...prev[name], id] : prev[name].filter((item) => item !== id),
    }));
    if (checked) {
      if (name === "rubroId") setValidationErrors((e) => ({ ...e, rubros: false }));
      if (name === "clienteId") setValidationErrors((e) => ({ ...e, clientes: false }));
    }
  }, []);
 
  // ── Compromisos / Obligaciones ─────────────────────────────────────────────
  const agregarCompromiso = () => {
    if (!compromisoActual.trim()) {
      mostrarNotificacion("La descripción del compromiso es obligatoria", "error");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      compromisos: [...prev.compromisos, { descripcion: compromisoActual.trim() }],
    }));
    setValidationErrors((e) => ({ ...e, compromisos: false }));
    setCompromisoActual("");
  };
 
  const eliminarCompromiso = (index) => {
    setFormData((prev) => {
      const arr = [...prev.compromisos];
      arr.splice(index, 1);
      return { ...prev, compromisos: arr };
    });
  };
 
  const agregarObligacion = () => {
    if (!obligacionActual.trim()) {
      mostrarNotificacion("La descripción de la obligación es obligatoria", "error");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      obligaciones: [...prev.obligaciones, { descripcion: obligacionActual.trim() }],
    }));
    setValidationErrors((e) => ({ ...e, obligaciones: false }));
    setObligacionActual("");
  };
 
  const eliminarObligacion = (index) => {
    setFormData((prev) => {
      const arr = [...prev.obligaciones];
      arr.splice(index, 1);
      return { ...prev, obligaciones: arr };
    });
  };
 
  const handleMostrarCompromisosChange = (e) => {
    const checked = e.target.checked;
    setMostrarSeccionCompromisos(checked);
    if (!checked) {
      setValidationErrors((e) => ({ ...e, compromisos: false }));
      setFormData((prev) => ({ ...prev, compromisos: [] }));
      setCompromisoActual("");
    }
  };
 
  const handleMostrarObligacionesChange = (e) => {
    const checked = e.target.checked;
    setMostrarSeccionObligaciones(checked);
    if (!checked) {
      setValidationErrors((e) => ({ ...e, obligaciones: false }));
      setFormData((prev) => ({ ...prev, obligaciones: [] }));
      setObligacionActual("");
    }
  };
 
  // ── Clientes automáticos ──────────────────────────────────────────────────
  const obtenerClientesAutomaticos = useCallback(async () => {
    if (!formData.organismoEmisorId || !formData.tipoDocumentoId || !formData.rubroId.length) {
      setClientesAutomaticos([]);
      return;
    }
    try {
      const clienteIds = await getClientesAutomaticos(
        localStorage.getItem("token"),
        formData.organismoEmisorId,
        formData.tipoDocumentoId,
        formData.rubroId
      );
      const completos = loadedData.clientes.filter((c) => clienteIds.includes(c.id));
      setClientesAutomaticos(completos);
      if (completos.length) {
        setFormData((prev) => ({ ...prev, clienteId: clienteIds }));
        setValidationErrors((e) => ({ ...e, clientes: false }));
      }
    } catch {
      setClientesAutomaticos([]);
    }
  }, [formData.organismoEmisorId, formData.tipoDocumentoId, formData.rubroId, loadedData.clientes]);
 
  useEffect(() => {
    if (formData.organismoEmisorId && formData.tipoDocumentoId && formData.rubroId.length > 0) {
      const id = setTimeout(obtenerClientesAutomaticos, 500);
      return () => clearTimeout(id);
    } else {
      setClientesAutomaticos([]);
    }
  }, [formData.organismoEmisorId, formData.rubroId.length, formData.tipoDocumentoId, obtenerClientesAutomaticos]);
 
  // ── Validación ─────────────────────────────────────────────────────────────
  const validateForm = () => {
    const errors = {
      rubros: formData.rubroId.length === 0,
      clientes: formData.clienteId.length === 0,
      clasificaciones: formData.clasificacionDocumentoId.length === 0,
      compromisos: mostrarSeccionCompromisos && formData.compromisos.length === 0,
      obligaciones: mostrarSeccionObligaciones && formData.obligaciones.length === 0,
    };
    setValidationErrors(errors);
    const hasBasicErrors =
      !formData.nombre ||
      !fecha ||
      !formData.tipoDocumentoId ||
      !formData.organismoEmisorId ||
      !formData.estadoTramitacionId ||
      !formData.url ||
      !formData.descripcion;
    return !hasBasicErrors && !errors.rubros && !errors.clientes && !errors.compromisos && !errors.obligaciones;
  };
 
  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validateForm()) {
      mostrarNotificacion("Por favor, completa todos los campos obligatorios", "error");
      return;
    }
    setIsLoading(true);
    try {
      const dataToSubmit = {
        ...formData,
        creadoPor: userId,
        fechaPublicacion: fecha ? fecha.toISOString() : null,
        compromisos: mostrarSeccionCompromisos ? formData.compromisos : [],
        obligaciones: mostrarSeccionObligaciones ? formData.obligaciones : [],
      };
      const response = await postDocumento(localStorage.getItem("token"), dataToSubmit);
      const nombreTruncado = truncarTexto(formData.nombre, 60);
      mostrarNotificacion(
        `La normativa "ID: ${response} - Nombre: ${nombreTruncado}" ha sido creada con éxito`,
        "success"
      );
      onSuccess?.();
      onClose();
    } catch {
      mostrarNotificacion("Error al registrar la normativa", "error");
    } finally {
      setIsLoading(false);
    }
  };
 
  // ── Quick-add helpers ──────────────────────────────────────────────────────
  const reloadData = () => {
    if (dataLoaderRef.current?.loadData) {
      dataLoaderRef.current.loadData();
    }
  };
 
  const handleOpenModal = (name) => setModalState((s) => ({ ...s, [name]: true }));
  const handleCloseModal = (name) => setModalState((s) => ({ ...s, [name]: false }));
 
  const makeQuickAddHandler = (postFn, modalName, labelField = "nombre") => async (data) => {
    try {
      setQuickAddLoading(true);
      await postFn(localStorage.getItem("token"), data);
      reloadData();
      handleCloseModal(modalName);
      mostrarNotificacion(`"${data[labelField]}" agregado con éxito`, "success");
    } catch {
      mostrarNotificacion(`Error al agregar elemento`, "error");
    } finally {
      setQuickAddLoading(false);
    }
  };
 
  // ── Fields para modales de quick-add ──────────────────────────────────────
  const QA_FIELDS = {
    organismoEmisor: [
      { name: "nombre", label: "Nombre", required: true },
      { name: "sigla", label: "Sigla", required: true },
      { name: "nombreAbreviado", label: "Nombre Abreviado", required: false },
    ],
    estadoTramitacion: [{ name: "nombre", label: "Nombre", required: true }],
    clasificacion: [{ name: "nombre", label: "Nombre", required: true }],
    rubro: [{ name: "nombre", label: "Nombre", required: true }],
    cliente: [
      { name: "nombre", label: "Nombre", required: true },
      { name: "tipoCorreo", label: "Tipo de Correo", required: true },
    ],
    pais: [
      { name: "nombre", label: "Nombre", required: true },
      { name: "codigoISO", label: "Código ISO", required: true },
    ],
  };
 
  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <DataLoader onDataLoaded={handleDataLoaded} ref={dataLoaderRef} />
 
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { borderRadius: 3, maxHeight: "95vh" } }}
      >
        {/* ── Header ── */}
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AutoAwesomeIcon color="primary" />
              <Typography variant="h6" component="span" fontWeight={700}>
                Registrar Normativa desde Scraper
              </Typography>
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
 
          {/* Badge de campos auto-mapeados */}
          {camposMapeados.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Alert
                severity="info"
                icon={<AutoAwesomeIcon fontSize="small" />}
                sx={{ py: 0.5, borderRadius: 2 }}
              >
                <Typography variant="body2" fontWeight={600} component="span">
                  Campos auto-completados:{" "}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                  {camposMapeados.map((campo) => (
                    <Chip key={campo} label={campo} size="small" color="info" variant="outlined" />
                  ))}
                </Box>
              </Alert>
            </Box>
          )}
        </DialogTitle>
 
        <Divider />
 
        {/* ── Contenido ── */}
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            {/* ── Columna izquierda: campos principales ── */}
            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
                {/* Nombre */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nombre"
                    name="nombre"
                    value={formData.nombre}
                    inputProps={{ maxLength: 100 }}
                    helperText={`${formData.nombre.length}/100 caracteres`}
                    onChange={handleNombreChange}
                    required
                  />
                </Grid>
 
                {/* Fecha */}
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      sx={{ width: "100%" }}
                      format="dd/MM/yyyy"
                      label="Fecha de publicación"
                      value={fecha}
                      onChange={setFecha}
                      slotProps={{ textField: { required: true, fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
 
                {/* Organismo emisor */}
                <Grid item xs={12} sm={6}>
                  <AutocompleteWithAddButton
                    options={loadedData.organismos}
                    label="Organismo emisor"
                    required
                    value={loadedData.organismos.find((o) => o.id === formData.organismoEmisorId) || null}
                    sx={{ mt: -1 }}
                    onChange={handleOrganismoChange}
                    onAddNewClick={() => handleOpenModal("organismoEmisor")}
                  />
                </Grid>
 
                {/* Tipo de documento */}
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    fullWidth
                    options={tiposDocumentoFiltrados}
                    getOptionLabel={(o) => o.nombre || ""}
                    value={tiposDocumentoFiltrados.find((t) => t.id === formData.tipoDocumentoId) || null}
                    onChange={(_, newValue) =>
                      handleChange({ target: { name: "tipoDocumentoId", value: newValue?.id || "" } })
                    }
                    disabled={!formData.organismoEmisorId}
                    sx={{ mt: 1, mb: 1 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tipo de Documento"
                        required
                        helperText={!formData.organismoEmisorId ? "Primero selecciona un organismo emisor" : ""}
                      />
                    )}
                    noOptionsText="No hay tipos de documento disponibles"
                  />
                </Grid>
 
                {/* Estado de tramitación */}
                <Grid item xs={12} sm={6}>
                  <AutocompleteWithAddButton
                    options={loadedData.estadosTramitacion}
                    label="Estado de tramitación"
                    required
                    value={loadedData.estadosTramitacion.find((e) => e.id === formData.estadoTramitacionId) || null}
                    sx={{ mt: -2 }}
                    onChange={(_, newValue) =>
                      handleChange({ target: { name: "estadoTramitacionId", value: newValue?.id || "" } })
                    }
                    onAddNewClick={() => handleOpenModal("estadoTramitacion")}
                  />
                </Grid>
 
                {/* URL */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="url"
                    label="URL"
                    name="url"
                    value={formData.url}
                    onChange={handleChange}
                    required
                  />
                </Grid>
 
                {/* Descripción */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    label="Descripción"
                    name="descripcion"
                    value={formData.descripcion}
                    onChange={handleDescripcionChange}
                    required
                  />
                </Grid>
 
                {/* Enlace resumen */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Enlace resumen"
                    name="enlaceResumen"
                    value={formData.enlaceResumen}
                    onChange={handleChange}
                  />
                </Grid>
 
                {/* País */}
                <Grid item xs={12} sm={6}>
                  <AutocompleteWithAddButton
                    options={loadedData.paises}
                    label="País"
                    required
                    value={loadedData.paises.find((p) => p.id === formData.paisId) || null}
                    sx={{ mt: -1 }}
                    onChange={(_, newValue) =>
                      handleChange({ target: { name: "paisId", value: newValue?.id || "" } })
                    }
                    onAddNewClick={() => handleOpenModal("pais")}
                  />
                </Grid>
 
                {/* Es actualización */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.actualizacionNorma}
                        onChange={handleChange}
                        name="actualizacionNorma"
                        type="checkbox"
                      />
                    }
                    label="Es actualización de normativa"
                  />
                </Grid>
              </Grid>
            </Grid>
 
            {/* ── Columna derecha: Aplica a ── */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 3,
                  height: "100%",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="h6">Aplica a</Typography>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenModal("clasificacion")}
                  >
                    Añadir
                  </Button>
                </Box>
                <CheckboxGroup
                  title=""
                  items={loadedData.clasificaciones}
                  selectedItems={formData.clasificacionDocumentoId}
                  onChange={handleCheckboxChange}
                  name="clasificacionDocumentoId"
                />
              </Box>
            </Grid>
 
            {/* ── Rubros ── */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: validationErrors.rubros ? "error.main" : "divider",
                  borderRadius: 3,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="h6" color={validationErrors.rubros ? "error" : "inherit"}>
                    Rubros
                    <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
                  </Typography>
                  <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenModal("rubro")}>
                    Añadir
                  </Button>
                </Box>
                <CheckboxGroup
                  title=""
                  items={loadedData.rubros}
                  selectedItems={formData.rubroId}
                  onChange={handleCheckboxChange}
                  name="rubroId"
                  showSelectAll
                  required
                  error={validationErrors.rubros}
                  helperText={validationErrors.rubros ? "Debe seleccionar al menos un rubro" : ""}
                />
              </Box>
            </Grid>
 
            {/* ── Clientes ── */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: validationErrors.clientes ? "error.main" : "divider",
                  borderRadius: 3,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="h6" color={validationErrors.clientes ? "error" : "inherit"}>
                    Clientes
                    <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
                    {clientesAutomaticos.length > 0 && (
                      <Typography component="span" sx={{ ml: 1, fontSize: "0.875rem", fontWeight: "normal", color: "success.main" }}>
                        ({clientesAutomaticos.length} preseleccionados)
                      </Typography>
                    )}
                  </Typography>
                  <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenModal("cliente")}>
                    Añadir
                  </Button>
                </Box>
                <CheckboxGroup
                  title=""
                  items={loadedData.clientes}
                  selectedItems={formData.clienteId}
                  onChange={handleCheckboxChange}
                  name="clienteId"
                  showSelectAll
                  required
                  error={validationErrors.clientes}
                  helperText={validationErrors.clientes ? "Debe seleccionar al menos un cliente" : ""}
                  layoutMode="grid"
                  columns={2}
                />
              </Box>
            </Grid>
 
            {/* ── Datos Newsletter ── */}
            <Grid item xs={12}>
              <Accordion defaultExpanded={false}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <PreviewIcon sx={{ mr: 1, color: "primary.main" }} />
                    <Typography variant="h6">Datos Newsletter</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Nombre Abreviado (Newsletter)"
                        name="nombreAbreviado"
                        value={formData.nombreAbreviado}
                        onChange={handleChange}
                        inputProps={{ maxLength: 100 }}
                        helperText={`${formData.nombreAbreviado.length}/100 caracteres`}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Vigencia"
                        name="vigencia"
                        value={formData.vigencia}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Descripción Abreviada (Newsletter)"
                        name="descripcionAbreviado"
                        value={formData.descripcionAbreviado}
                        onChange={handleChange}
                        inputProps={{ maxLength: 400 }}
                        helperText={`${formData.descripcionAbreviado.length}/400 caracteres`}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
 
            {/* ── Compromisos y Obligaciones ── */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Compromisos y Obligaciones
                </Typography>
 
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={mostrarSeccionCompromisos}
                          onChange={handleMostrarCompromisosChange}
                        />
                      }
                      label={
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          ¿Deseas añadir compromisos a esta normativa?
                          {mostrarSeccionCompromisos && (
                            <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
                          )}
                        </Box>
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={mostrarSeccionObligaciones}
                          onChange={handleMostrarObligacionesChange}
                        />
                      }
                      label={
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          ¿Deseas añadir obligaciones a esta normativa?
                          {mostrarSeccionObligaciones && (
                            <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
                          )}
                        </Box>
                      }
                    />
                  </Grid>
 
                  {/* Sección Compromisos */}
                  {mostrarSeccionCompromisos && (
                    <>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="h6" color={validationErrors.compromisos ? "error" : "inherit"} gutterBottom>
                          Agregar Compromisos
                          <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
                        </Typography>
                        {validationErrors.compromisos && (
                          <Typography variant="caption" color="error" display="block">
                            Debe agregar al menos un compromiso
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={8}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Descripción del compromiso"
                          value={compromisoActual}
                          onChange={(e) => setCompromisoActual(e.target.value)}
                          error={validationErrors.compromisos}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Button
                          variant="contained"
                          onClick={agregarCompromiso}
                          startIcon={<AddIcon />}
                          fullWidth
                          sx={{ height: "56px" }}
                        >
                          Añadir compromiso
                        </Button>
                      </Grid>
                      {formData.compromisos.length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            Compromisos agregados ({formData.compromisos.length})
                          </Typography>
                          {formData.compromisos.map((c, i) => (
                            <Card key={i} variant="outlined" sx={{ mb: 1 }}>
                              <CardContent sx={{ pb: 1 }}>
                                <Typography variant="body2">{c.descripcion}</Typography>
                              </CardContent>
                              <CardActions sx={{ pt: 0 }}>
                                <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => eliminarCompromiso(i)}>
                                  Eliminar
                                </Button>
                              </CardActions>
                            </Card>
                          ))}
                        </Grid>
                      )}
                    </>
                  )}
 
                  {/* Sección Obligaciones */}
                  {mostrarSeccionObligaciones && (
                    <>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="h6" color={validationErrors.obligaciones ? "error" : "inherit"} gutterBottom>
                          Agregar Obligaciones
                          <Typography component="span" color="error" sx={{ ml: 0.5 }}>*</Typography>
                        </Typography>
                        {validationErrors.obligaciones && (
                          <Typography variant="caption" color="error" display="block">
                            Debe agregar al menos una obligación
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} sm={8}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Descripción de la obligación"
                          value={obligacionActual}
                          onChange={(e) => setObligacionActual(e.target.value)}
                          error={validationErrors.obligaciones}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Button
                          variant="contained"
                          color="secondary"
                          onClick={agregarObligacion}
                          startIcon={<AddIcon />}
                          fullWidth
                          sx={{ height: "56px" }}
                        >
                          Añadir obligación
                        </Button>
                      </Grid>
                      {formData.obligaciones.length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            Obligaciones agregadas ({formData.obligaciones.length})
                          </Typography>
                          {formData.obligaciones.map((o, i) => (
                            <Card key={i} variant="outlined" sx={{ mb: 1 }}>
                              <CardContent sx={{ pb: 1 }}>
                                <Typography variant="body2">{o.descripcion}</Typography>
                              </CardContent>
                              <CardActions sx={{ pt: 0 }}>
                                <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => eliminarObligacion(i)}>
                                  Eliminar
                                </Button>
                              </CardActions>
                            </Card>
                          ))}
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
 
        <Divider />
 
        {/* ── Footer ── */}
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ minWidth: 180 }}
          >
            {isLoading ? "Registrando..." : "Registrar Normativa"}
          </Button>
        </DialogActions>
      </Dialog>
 
      {/* ── Modales quick-add ── */}
      <MantenedorQuickAddModal
        open={modalState.organismoEmisor}
        onClose={() => handleCloseModal("organismoEmisor")}
        onSubmit={makeQuickAddHandler(postOrganismoEmisor, "organismoEmisor")}
        title="Nuevo Organismo Emisor"
        fields={QA_FIELDS.organismoEmisor}
        isLoading={quickAddLoading}
      />
      <MantenedorQuickAddModal
        open={modalState.estadoTramitacion}
        onClose={() => handleCloseModal("estadoTramitacion")}
        onSubmit={makeQuickAddHandler(postEstadoTramitacion, "estadoTramitacion")}
        title="Nuevo Estado de Tramitación"
        fields={QA_FIELDS.estadoTramitacion}
        isLoading={quickAddLoading}
      />
      <MantenedorQuickAddModal
        open={modalState.clasificacion}
        onClose={() => handleCloseModal("clasificacion")}
        onSubmit={makeQuickAddHandler(postClasificacionDocumentos, "clasificacion")}
        title="Nueva Clasificación"
        fields={QA_FIELDS.clasificacion}
        isLoading={quickAddLoading}
      />
      <MantenedorQuickAddModal
        open={modalState.rubro}
        onClose={() => handleCloseModal("rubro")}
        onSubmit={makeQuickAddHandler(postRubro, "rubro")}
        title="Nuevo Rubro"
        fields={QA_FIELDS.rubro}
        isLoading={quickAddLoading}
      />
      <MantenedorQuickAddModal
        open={modalState.cliente}
        onClose={() => handleCloseModal("cliente")}
        onSubmit={makeQuickAddHandler(postClientes, "cliente")}
        title="Nuevo Cliente"
        fields={QA_FIELDS.cliente}
        isLoading={quickAddLoading}
      />
      <MantenedorQuickAddModal
        open={modalState.pais}
        onClose={() => handleCloseModal("pais")}
        onSubmit={makeQuickAddHandler(postPais, "pais")}
        title="Nuevo País"
        fields={QA_FIELDS.pais}
        isLoading={quickAddLoading}
      />
    </>
  );
};
 
export default RegistrarDesdeScraperModal;